import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { WizardModal } from '~/eds/src/components/Modal/subcomponents/WizardModal';
import { FormSection } from '~/features/sso-settings/FormSection';
import { isFieldValid } from '~/features/sso-settings/FormSection.utils';
import { FormSection as FormSectionType } from '~/types/SSOForm';

type Props = {
  clientId: string;
  sections: FormSectionType[];
  onHide: () => void;
  onSubmit: () => void;
};

export const SAMLConfigWizard = ({
  clientId,
  sections,
  onHide,
  onSubmit,
}: Props) => {
  const [activeStepId, setActiveStepId] = useState(sections[0].id);
  const [activeInstructionId, setActiveInstructionId] = useState<string>();
  const [lastCompletedStepId, setLastCompletedStepId] = useState<string>();
  const intl = useIntl();
  const activeIndex = useMemo(
    () => sections.findIndex((step) => step.id === activeStepId),
    [activeStepId],
  );
  const currentSection = useMemo(
    () => sections[activeIndex],
    [activeIndex, sections],
  );

  const isSectionValid = useMemo(
    () => currentSection.configs.every((formField) => isFieldValid(formField)),
    [currentSection],
  );

  const handleNext = () => {
    if (activeIndex === sections.length - 1) {
      onSubmit();
      return;
    }

    if (isSectionValid) {
      setLastCompletedStepId(activeStepId);
      setActiveStepId(sections[activeIndex + 1].id);
    }
  };

  const handleBack = () => {
    if (activeIndex === 0) return;
    setActiveStepId(sections[activeIndex - 1].id);
  };

  const handleSelectStep = (stepId: string) => {
    const stepIndex = sections.findIndex((step) => step.id === stepId);
    const lastCompletedStepIdIndex = sections.findIndex(
      (step) => step.id === lastCompletedStepId,
    );
    if (stepIndex < activeIndex || stepIndex <= lastCompletedStepIdIndex) {
      setActiveStepId(stepId);
    }
  };

  return (
    <WizardModal
      title={intl.formatMessage({
        id: 'SamlConfigWizard.title',
        defaultMessage: 'Configure SAML SSO',
        description: 'Title for SAML configuration wizard',
      })}
      isVisible={true}
      backAction={{
        text: intl.formatMessage({
          id: 'SamlConfigWizard.backButton',
          defaultMessage: 'Back',
          description: 'Back button text',
        }),
        onClick: handleBack,
      }}
      steps={{
        activeInstructionId,
        activeStepId,
        lastCompletedStepId,
        steps: sections,
        onSelectStep: handleSelectStep,
        title: intl.formatMessage({
          id: 'SamlConfigWizard.stepsTitle',
          defaultMessage: 'Steps',
          description: 'Title for steps section',
        }),
      }}
      primaryAction={{
        text:
          activeIndex === sections.length - 1
            ? intl.formatMessage({
                id: 'SamlConfigWizard.submitButton',
                defaultMessage: 'Submit',
                description: 'Submit button text for final step',
              })
            : intl.formatMessage({
                id: 'SamlConfigWizard.continueButton',
                defaultMessage: 'Continue',
                description: 'Continue button text for non-final steps',
              }),
        onClick: handleNext,
        disabled: !isSectionValid,
      }}
      onHide={onHide}
    >
      <FormSection
        clientId={clientId}
        section={currentSection}
        onExplain={setActiveInstructionId}
      />
    </WizardModal>
  );
};
